import React, { useState } from 'react';
import { Popover, MenuList, Drawer } from 'aha-components';
import Avatar from './Avatar';
import {
  CreditScoreIcon,
  ExitIcon,
  GooglePlayIcon,
  HelpCenterIcon,
  InfoCircleIcon,
  RightArrowIcon,
  SettingsIcon,
  SupportIcon,
  WebsiteIcon
} from '../common/icon';
import AccountMenuIcon from './AccountMenuIcon';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useClerk, useUser } from '@clerk/clerk-react';
import { clearToken, getClerkSignOutRedirectUrl, isFromApp } from '@/biz/tool';
import { langList } from '@/i18n';
import { handleI18nChange } from '@/i18n/utils';
import { Tracker } from '@/utils';
import { PLAY_STORE_PACKAGE } from '@/utils/config';
import { getAhaBridge, hasNativeAhaBridge } from '@/app/bridge/ahaBridge';

export default function AvatarPanel({
  setMenuOpen,
  compact
}: {
  setMenuOpen?: (open: boolean) => void;
  /** 收起侧栏时仅展示圆形头像，宽度与 64px 栏匹配 */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showLanguageDrawer, setShowLanguageDrawer] = useState(false);
  const showNativeAbout = hasNativeAhaBridge();
  const closePopover = () => setOpen(false);
  const { user } = useUser();
  const clerk = useClerk();

  const handleSignOut = async () => {
    if (isFromApp()) {
      await clerk.signOut();
      // 清理 H5 壳层会话态，避免回到 WebView 时残留旧数据。
      clearToken();
      window.localStorage.removeItem('auth_redirect');
      window.localStorage.removeItem('profile');
      window.localStorage.removeItem('platform');
      window.sessionStorage.removeItem('aha_signup_referral_code_input');
      navigate('/login?from=app', { replace: true });
      return;
    }

    clearToken();
    await clerk.signOut({ redirectUrl: getClerkSignOutRedirectUrl() });
  };

  const handleGooglePlayClick = () => {
    const clickId = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const referrer = new URLSearchParams({
      utm_source: 'download_app_banner',
      utm_medium: 'app_banner',
      utm_campaign: user?.id || '',
      banner_source: 'download_app_banner',
      banner_id: 'download_app_banner',
      click_id: clickId,
      user_id: user?.id || ''
    }).toString();

    Tracker.gtag('event', 'click', {
      event_category: 'download_app_banner_click',
      utm_source: 'navbar',
      utm_medium: 'navbar',
      utm_campaign: user?.id || '',
      click_id: clickId
    });
    const url = `https://play.google.com/store/apps/details?id=${PLAY_STORE_PACKAGE}&referrer=${encodeURIComponent(referrer)}`;
    window.open(url, '_blank');
  };

  const handleNativeAboutClick = async () => {
    try {
      await getAhaBridge().runtime.openAbout();
    } catch (error) {
      console.error('[AhaBridge] failed to show native about', error);
    }
  };

  const items = [
    {
      children: [
        {
          icon: (
            <AccountMenuIcon
              name="settings"
              className="h-4 w-4 text-[#A3A3A3]"
            />
          ),
          key: 'settings',
          label: t('navbar.menu.settings', { defaultValue: 'Settings' }),
          onClick: () => {
            navigate('/settings');
            closePopover();
          }
        },
        {
          key: 'language',
          render: () => (
            <div className="flex h-[38px] items-center gap-2 px-4 py-[9px]">
              <AccountMenuIcon
                name="language"
                className="h-4 w-4 shrink-0 text-[#A3A3A3]"
              />
              <Popover
                className="w-full"
                content={
                  <ul className="px-[8px] py-[16px]">
                    {langList.map((item) => (
                      <li
                        key={item.detectedValue}
                        onClick={() => handleI18nChange(item.detectedValue)}
                        className="px-[16px] py-[8px] rounded-[16px] hover:bg-[#F9F9FB] cursor-pointer"
                      >
                        <span className="text-sm font-medium font-['Inter'] leading-[22px]">
                          {item.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                }
                placement="right"
                trigger="click"
                width={228}
              >
                <div className="flex w-full items-center justify-between font-['Inter'] text-sm font-semibold leading-5 text-[#404040]">
                  {t('settings.menu.language', { defaultValue: 'Language' })}
                  <AccountMenuIcon
                    name="chevron-right"
                    className="h-4 w-4 text-[#A3A3A3]"
                  />
                </div>
              </Popover>
            </div>
          )
        },
        {
          icon: (
            <AccountMenuIcon
              name="help-center"
              className="h-4 w-4 text-[#A3A3A3]"
            />
          ),
          key: 'help-center',
          label: t('navbar.help.text', { defaultValue: 'Help Center' }),
          onClick: () => {
            window.open(
              'https://www.ahacreator.com/creators/resources/help-center',
              '_blank'
            );
            closePopover();
          }
        },
        {
          icon: (
            <AccountMenuIcon
              name="support"
              className="h-4 w-4 text-[#A3A3A3]"
            />
          ),
          key: 'support',
          label: t('navbar.menu.support', { defaultValue: 'Support' }),
          onClick: () => {
            window.open('mailto:support@creator.ahacreator.com', '_blank');
            closePopover();
          }
        },
        {
          icon: (
            <AccountMenuIcon name="rating" className="h-4 w-4 text-[#A3A3A3]" />
          ),
          key: 'credit-score',
          label: t('navbar.menu.credit_score', {
            defaultValue: 'Rating and Review'
          }),
          onClick: () => {
            navigate('/reviews');
            closePopover();
          }
        }
      ]
    },
    {
      children: [
        {
          key: 'logout',
          render: () => (
            <button
              type="button"
              className="flex h-[38px] w-full items-center gap-2 border-0 bg-transparent px-4 py-[9px] text-left"
              onClick={handleSignOut}
            >
              <AccountMenuIcon
                name="logout"
                className="h-4 w-4 shrink-0 text-[#A3A3A3]"
              />
              <div className="font-['Inter'] text-sm font-semibold leading-5 text-[#404040]">
                {t('navbar.menu.log_out', { defaultValue: 'Sign out' })}
              </div>
            </button>
          )
        },
        ...(showNativeAbout
          ? [
              {
                icon: (
                  <InfoCircleIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
                ),
                key: 'native-about',
                label: t('navbar.menu.about_app', {
                  defaultValue: 'About'
                }),
                onClick: () => {
                  void handleNativeAboutClick();
                }
              }
            ]
          : [])
      ]
    }
  ];

  return (
    <>
      <Popover
        open={open}
        onOpenChange={setOpen}
        content={
          <div className="w-full overflow-hidden bg-white">
            {compact && (
              <div className="shadow-[inset_0_-1px_0_#E5E5E5]">
                <Avatar menuHeader />
              </div>
            )}
            <MenuList items={items as any} width="100%" />
            <div className="flex h-11 items-center border-t border-[#E5E5E5] px-4 py-3 font-['Inter'] text-sm font-normal leading-5 !text-[#737373]">
              © ahacreator
            </div>
          </div>
        }
        placement="right"
        trigger="click"
        width={228}
        className="!hidden md:!block"
        popoverClassName="creator-account-popover"
      >
        <button
          type="button"
          className={`border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-[var(--creator-sidebar-icon)] ${
            compact ? 'inline-flex' : 'w-full'
          }`}
          aria-label={t('navbar.menu.account', {
            defaultValue: 'Account menu'
          })}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <Avatar compact={compact} />
        </button>
      </Popover>
      <div className="md:hidden">
        <button
          type="button"
          className="w-full border-0 bg-transparent p-0"
          onClick={() => setIsDrawerOpen(true)}
        >
          <Avatar compact={compact} />
        </button>
        <Drawer
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          zIndex={1400}
          height="auto"
          placement="bottom"
          closable={false}
          drawerStyle={{
            borderRadius: '16px 16px 0 0',
            maxHeight: 'calc(100dvh - 16px)',
            overflow: 'hidden'
          }}
          bodyStyle={{
            padding: 0,
            flex: 'none',
            maxHeight: 'calc(100dvh - 16px)',
            overflowY: 'auto'
          }}
        >
          <div className="flex flex-col gap-3 pt-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))]">
            <div className="px-4 flex flex-col gap-3">
              <div
                className="flex items-center cursor-pointer gap-3 px-[14px] h-[36px]"
                onClick={() => {
                  navigate('/settings');
                  setMenuOpen?.(false);
                  setIsDrawerOpen(false);
                }}
              >
                <SettingsIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
                <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5">
                  {t('navbar.menu.settings', { defaultValue: 'Settings' })}
                </div>
              </div>
              <div
                className="flex items-center cursor-pointer gap-3 px-[14px] h-[36px]"
                onClick={() => {
                  setShowLanguageDrawer(true);
                }}
              >
                <WebsiteIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
                <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5 flex items-center justify-between w-full">
                  {t('settings.menu.language', { defaultValue: 'Language' })}
                  <RightArrowIcon className="w-5 h-5 text-[#79716B]" />
                </div>
              </div>
              <div
                className="flex items-center cursor-pointer gap-3 px-[14px] h-[36px]"
                onClick={() => {
                  window.open(
                    'https://www.ahacreator.com/creators/resources/help-center',
                    '_blank'
                  );
                  setIsDrawerOpen(false);
                }}
              >
                <HelpCenterIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
                <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5">
                  {t('navbar.help.text', { defaultValue: 'Help Center' })}
                </div>
              </div>
              <div
                className="flex items-center cursor-pointer gap-3 px-[14px] h-[36px]"
                onClick={() => {
                  window.open(
                    'mailto:support@creator.ahacreator.com',
                    '_blank'
                  );
                  setIsDrawerOpen(false);
                }}
              >
                <SupportIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
                <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5">
                  {t('navbar.menu.support', { defaultValue: 'Support' })}
                </div>
              </div>
              <div
                className="flex items-center cursor-pointer gap-3 px-[14px] h-[36px]"
                onClick={() => {
                  navigate('/reviews');
                  setMenuOpen?.(false);
                  setIsDrawerOpen(false);
                }}
              >
                <CreditScoreIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
                <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5">
                  {t('navbar.menu.credit_score', {
                    defaultValue: 'Rating and Review'
                  })}
                </div>
              </div>
            </div>
            <div className="border-t border-borderColor"></div>
            <div
              className="flex items-center cursor-pointer gap-3 py-3 px-[30px]"
              onClick={handleSignOut}
            >
              <ExitIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
              <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5">
                {t('navbar.menu.log_out', { defaultValue: 'Sign out' })}
              </div>
            </div>
            {showNativeAbout && (
              <div
                className="flex items-center cursor-pointer gap-3 py-3 px-[30px]"
                onClick={() => {
                  void handleNativeAboutClick();
                }}
              >
                <InfoCircleIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
                <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5">
                  {t('navbar.menu.about_app', { defaultValue: 'About' })}
                </div>
              </div>
            )}
          </div>
        </Drawer>
        <Drawer
          open={showLanguageDrawer}
          onClose={() => setShowLanguageDrawer(false)}
          zIndex={1401}
          height="auto"
          placement="bottom"
          closable={false}
          drawerStyle={{
            borderRadius: '16px 16px 0 0',
            maxHeight: 'calc(100dvh - 16px)',
            overflow: 'hidden'
          }}
          bodyStyle={{
            padding: 0,
            flex: 'none',
            maxHeight: 'calc(100dvh - 16px)',
            overflowY: 'auto'
          }}
        >
          <ul className="px-[8px] pt-[16px] pb-[calc(16px+env(safe-area-inset-bottom,0px))]">
            {langList.map((item) => (
              <li
                key={item.detectedValue}
                onClick={() => handleI18nChange(item.detectedValue)}
                className="px-[16px] py-[8px] rounded-[16px] hover:bg-[#F9F9FB] cursor-pointer"
              >
                <span className="text-sm font-medium font-['Inter'] leading-[22px]">
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
        </Drawer>
      </div>
    </>
  );
}
